/**
 * About Bundle (web and desktop; "About & updates" canvas, About + States artboards): the version and channel, the
 * update status box (Check again / Restart to update / Reload to update / Try again), the desktop app, web build and
 * server rows, and Copy version info. Opened from the Help menu, the macOS app menu and Check for updates….
 * Also the Help menu rows for updates (`useUpdateMenuEntries`) and the "Updated to …" toast.
 */
import { Updates, type UpdateStatus } from "@bundle/core";
import { Button, Icon, Logo, Progress, Sheet, Stack, Text, Toast, useLocale, useT, type MenuEntry } from "@bundle/ui";
import { useState, useSyncExternalStore, type ReactNode } from "react";
import { about, updatedToast, updateStatus, versionInfo } from "../platform/updates.ts";

function useStatus(): UpdateStatus {
  return useSyncExternalStore(updateStatus.subscribe, updateStatus.getSnapshot, updateStatus.getSnapshot);
}

/** The status box at the top of About (States artboard). */
function StatusBox({ status, web }: { status: UpdateStatus; web: boolean }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const restart = () => {
    if (busy) return;
    setBusy(true);
    updateStatus.restartNow().catch(() => setBusy(false));
  };
  const check = () => void updateStatus.checkNow();
  let icon: ReactNode = <Icon name="checkCircle" size="icon.md" color="status.success" />;
  let title = t("update.upToDate");
  let note: string | undefined;
  let action: ReactNode = <Button label={t("update.checkAgain")} variant="secondary" size="sm" onPress={check} testID="about-check" />;
  let tone: "plain" | "danger" = "plain";
  let progress: ReactNode = null;
  switch (status.kind) {
    case "checking":
      icon = <Icon name="hourglass" size="icon.md" color="text.tertiary" />;
      title = t("update.checking");
      action = null;
      break;
    case "idle":
      note = t("update.autoNote");
      break;
    case "upToDate":
      title = web ? t("update.upToDate") : t("update.upToDateVersion", { version: status.version });
      note = web && status.version !== "" ? t("update.webLatest", { commit: status.version }) : t("update.checkedJustNow");
      break;
    case "downloading": {
      const percent = Updates.progressPercent(status);
      icon = <Icon name="download" size="icon.md" color="text.link" />;
      title = t("update.availableVersion", { version: status.version });
      note = percent === undefined ? t("update.downloadingPlain") : t("update.downloadingPercent", { percent });
      action = null;
      progress = <Progress value={percent} accessibilityLabel={title} testID="about-progress" />;
      break;
    }
    case "ready":
      icon = <Icon name="reload" size="icon.md" color="text.link" />;
      title = web ? t("update.webReadyTitle") : t("update.readyVersion", { version: status.version });
      note = web ? t("update.webReadyNote") : t("update.readyNote");
      action = <Button label={web ? t("update.reloadToUpdate") : t("update.restartToUpdate")} variant="primary" size="sm" loading={busy} onPress={restart} testID="about-restart" />;
      break;
    case "error":
      icon = <Icon name="alert" size="icon.md" color="status.danger" />;
      title = status.during === "check" ? t("update.errorCheck") : status.during === "download" ? t("update.errorDownload") : t("update.errorInstall");
      note = t("update.errorHint");
      tone = "danger";
      action = <Button label={t("update.tryAgain")} variant="secondary" size="sm" onPress={check} testID="about-retry" />;
      break;
  }
  const notes = status.kind === "downloading" || status.kind === "ready" ? Updates.shortNotes(status.notes) : "";
  return (
    <Stack
      direction="horizontal"
      gap="3"
      align="center"
      paddingX="3.5"
      paddingY="3"
      radius="md"
      background={tone === "danger" ? "status.danger.bg" : "bg.surface"}
      border={tone === "danger" ? "status.danger.border" : "border.subtle"}
      testID={`about-status-${status.kind}`}
    >
      {icon}
      <Stack gap="1" grow>
        <Text variant="footnote" weight="semibold" color={tone === "danger" ? "status.danger.fg" : "text.primary"}>
          {title}
        </Text>
        {note === undefined ? null : (
          <Text variant="caption" color={tone === "danger" ? "status.danger.fg" : "text.tertiary"}>
            {note}
          </Text>
        )}
        {notes === "" ? null : (
          <Text variant="caption" color="text.secondary" lines={2}>
            {notes}
          </Text>
        )}
        {progress}
      </Stack>
      {action}
    </Stack>
  );
}

function Row({ label, value, testID }: { label: string; value: string; testID: string }) {
  if (value === "") return null;
  return (
    <Stack direction="horizontal" align="center" gap="3" paddingX="3.5" paddingY="2" testID={testID}>
      <Stack width="property.label">
        <Text variant="footnote" color="text.secondary">
          {label}
        </Text>
      </Stack>
      <Text variant="caption" mono selectable>
        {value}
      </Text>
    </Stack>
  );
}

export function AboutDialog({ onWhatsNew }: { onWhatsNew?: () => void }) {
  const t = useT();
  const locale = useLocale();
  const open = useSyncExternalStore(about.subscribe, about.getSnapshot, about.getSnapshot);
  const status = useStatus();
  const [copied, setCopied] = useState(false);
  if (!open) return null;
  const info = versionInfo();
  const web = info.platform === "web";
  const channel = t(`about.channel.${info.channel}`);
  const version = Updates.displayVersion(info);
  const copy = () => {
    void navigator.clipboard?.writeText(Updates.versionReport(info)).then(() => setCopied(true));
  };
  const app = info.app === undefined ? "" : [info.app.version, info.app.os === undefined || info.app.os === "" ? undefined : `(${info.app.os})`].filter((s) => s !== undefined).join(" ");
  return (
    <>
      <Sheet open onClose={() => about.close()} title={t("about.title")} closeLabel={t("common.close")} testID="about">
        <Stack gap="4">
          <Stack direction="horizontal" gap="3" align="center">
            <Logo variant="symbol" size="avatar.lg" />
            <Stack gap="0.5">
              <Text variant="title3" weight="semibold" heading={2}>
                Bundle
              </Text>
              <Text variant="footnote" color="text.secondary" testID="about-version">
                {version === "" ? channel : t("about.versionLine", { version, channel })}
              </Text>
            </Stack>
          </Stack>
          <StatusBox status={status} web={web} />
          <Stack radius="md" border="border.subtle">
            <Row label={t("about.desktopApp")} value={app} testID="about-app" />
            <Row label={t("about.webApp")} value={Updates.formatBuild(info.web, locale)} testID="about-web" />
            <Row label={t("about.server")} value={Updates.formatBuild(info.server, locale)} testID="about-server" />
          </Stack>
          <Stack direction="horizontal" justify="between" align="center">
            {onWhatsNew === undefined ? <span /> : <Button label={t("update.whatsNew")} variant="ghost" size="sm" onPress={onWhatsNew} testID="about-whats-new" />}
            <Button label={t("about.copy")} icon="copy" variant="ghost" size="sm" onPress={copy} testID="about-copy" />
          </Stack>
        </Stack>
      </Sheet>
      <Toast open={copied} onClose={() => setCopied(false)} message={t("about.copied")} icon={{ name: "checkCircle", tone: "success" }} closeLabel={t("common.dismiss")} durationMs={2500} testID="about-copied" />
    </>
  );
}

/** "Updated to Bundle 0.2.21." after an automatic restart or reload. */
export function UpdatedToast({ onWhatsNew }: { onWhatsNew?: () => void }) {
  const t = useT();
  const version = useSyncExternalStore(updatedToast.subscribe, updatedToast.getSnapshot, updatedToast.getSnapshot);
  if (version === undefined) return null;
  return (
    <Toast
      open
      onClose={() => updatedToast.dismiss()}
      icon={{ name: "sparkles" }}
      message={t("update.updatedTo", { version })}
      action={
        onWhatsNew === undefined
          ? undefined
          : {
              label: t("update.whatsNew"),
              onPress: () => {
                updatedToast.dismiss();
                onWhatsNew();
              },
              testID: "updated-whats-new",
            }
      }
      closeLabel={t("common.dismiss")}
      durationMs={8000}
      testID="updated-toast"
    />
  );
}

/**
 * The Help menu's update rows (after Send feedback…): a separator, Check for updates… / Downloading 0.2.21… 42% /
 * Restart to update, then About Bundle. `badge`: the Help button's dot (an update is waiting).
 */
export function useUpdateMenuEntries(): { entries: MenuEntry[]; badge: boolean; aboutHint: string } {
  const t = useT();
  const status = useStatus();
  const row = Updates.menuRow(status);
  const web = versionInfo().platform === "web";
  const update: MenuEntry =
    row.kind === "restart"
      ? { id: "help-update", label: web ? t("update.reloadToUpdate") : t("update.restartToUpdate"), icon: "reload", onSelect: () => void updateStatus.restartNow() }
      : row.kind === "downloading"
        ? {
            id: "help-update",
            label: `${t("update.downloadingMenu", { version: row.version })}${row.percent === undefined ? "" : ` ${row.percent}%`}`,
            icon: "download",
            onSelect: () => about.open(),
          }
        : {
            id: "help-update",
            label: t("update.check"),
            icon: "reload",
            onSelect: () => {
              about.open();
              void updateStatus.checkNow();
            },
          };
  const entries: MenuEntry[] = [{ id: "help-update-sep", separator: true }, update, { id: "help-about", label: t("about.title"), icon: "alert", onSelect: () => about.open() }];
  return { entries, badge: Updates.showsBadge(status), aboutHint: Updates.displayVersion(versionInfo()) };
}
